<script setup>
import axios from 'axios';
import { computed, onMounted, ref, watch } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import Icon from '@/Components/Icon.vue';
import {
    getMaintenanceReferenceData,
    maintenanceDraftKey,
    putMaintenanceReferenceData,
} from '@/modules/hera-maintenance/offline/maintenanceDb';
import { useMaintenanceOfflineForm } from '@/modules/hera-maintenance/offline/useMaintenanceOfflineForm';

const props = defineProps({
    interventionId: { type: [String, Number], required: true },
    scope: { type: Object, required: true },
    offlineEnabled: { type: Boolean, default: false },
});

const emit = defineEmits(['completed']);
const draftKey = maintenanceDraftKey({
    sheetName: 'maintenance_intervention_completion',
    recordId: props.interventionId,
});
const endpoint = `/api/hera/maintenance/interventions/${props.interventionId}/complete`;
const completionCacheKey = `intervention-completion:${props.interventionId}`;
const offline = useMaintenanceOfflineForm({
    scope: props.scope,
    draftKey,
    sheetName: 'maintenance_intervention_completion',
    endpoint,
});

const loading = ref(true);
const completionData = ref(null);
const selectedIds = ref([]);
const resolutionNotes = ref('');
const requestUuid = ref(uuidv4());
const errorMessage = ref('');
const submitting = ref(false);
const expanded = ref(false);

function normalizeSelectedIds(value) {
    if (Array.isArray(value)) return value;
    if (value === null || typeof value === 'undefined' || value === '') return [];
    return [value];
}

function isSelected(degradationId) {
    return selectedIds.value.some(selectedId => String(selectedId) === String(degradationId));
}

const openDegradations = computed(() => completionData.value?.degradations?.filter(
    degradation => degradation.status !== 'resolved',
) ?? []);
const conflicts = computed(() => completionData.value?.degradations?.filter(
    degradation => degradation.status === 'resolved',
) ?? []);
const hasCompletionError = computed(() => Boolean(errorMessage.value || offline.lastError.value?.message));
const missingEvidence = computed(() => (
    !completionData.value?.endDate || Number(completionData.value?.photoCount ?? 0) < 1
));
const compactStatus = computed(() => {
    if (loading.value) return 'Caricamento...';
    if (hasCompletionError.value) return 'Completamento non disponibile';
    if (offline.pending.value) {
        return offline.status.value === 'conflict' ? 'Conflitto da verificare' : 'Richiesta salvata sul dispositivo';
    }
    if (completionData.value?.status === 'completed') return 'Intervento completato';
    if (missingEvidence.value) return 'Mancano data di fine o fotografia';
    if (openDegradations.value.length === 1) return '1 degrado da risolvere';
    if (openDegradations.value.length > 1) return `${openDegradations.value.length} degradi da risolvere`;
    return 'Pronto per il completamento';
});
const canSubmit = computed(() => !submitting.value
    && !offline.syncing.value
    && completionData.value?.status !== 'completed'
    && !missingEvidence.value
    && (props.offlineEnabled || offline.online.value));

async function loadCompletionData() {
    const cached = props.offlineEnabled
        ? await getMaintenanceReferenceData(props.scope, completionCacheKey).catch(() => null)
        : null;
    try {
        const response = await axios.get(`/api/hera/maintenance/interventions/${props.interventionId}/completion`);
        completionData.value = response.data.data;
        if (props.offlineEnabled) {
            await putMaintenanceReferenceData(props.scope, completionCacheKey, response.data.data);
        }
    } catch (error) {
        if (cached?.payload) {
            completionData.value = cached.payload;
        } else {
            errorMessage.value = error?.response?.data?.message ?? 'Impossibile preparare il completamento.';
        }
    }
}

async function initialize() {
    const draft = props.offlineEnabled ? await offline.initialize() : null;
    if (draft?.data) {
        selectedIds.value = normalizeSelectedIds(draft.data.degradation_ids);
        resolutionNotes.value = draft.data.resolution_notes ?? '';
        requestUuid.value = draft.clientUuid ?? requestUuid.value;
    }
    await loadCompletionData();
    if (!draft?.data && completionData.value) {
        selectedIds.value = openDegradations.value.map(degradation => degradation.id);
    }
    loading.value = false;
}

async function complete() {
    if (!canSubmit.value) return;
    submitting.value = true;
    errorMessage.value = '';
    const payload = {
        request_uuid: requestUuid.value,
        degradation_ids: [...normalizeSelectedIds(selectedIds.value)],
        resolution_notes: resolutionNotes.value || null,
    };

    try {
        if (props.offlineEnabled) {
            await offline.queue(payload, requestUuid.value);
        } else {
            const response = await axios.post(endpoint, payload);
            emit('completed', response.data.data);
        }
    } catch (error) {
        errorMessage.value = error?.response?.data?.message ?? error.message;
    } finally {
        submitting.value = false;
    }
}

watch(offline.lastResponse, response => {
    if (response?.data?.data) emit('completed', response.data.data);
});

onMounted(initialize);
</script>

<template>
    <section class="mb-2 border-y border-[#312D32]/15 bg-[#F1F0EC]"
        aria-labelledby="maintenance-completion-title">
        <button type="button" aria-controls="maintenance-completion-content" :aria-expanded="expanded"
            :aria-label="`${expanded ? 'Riduci' : 'Apri'} completamento intervento`"
            class="flex min-h-11 w-full items-center gap-3 px-3 py-2 text-left md:px-4"
            @click="expanded = !expanded">
            <div class="min-w-0 flex-1">
                <h2 id="maintenance-completion-title" class="truncate text-xs font-semibold uppercase">
                    Completamento intervento
                </h2>
                <p class="truncate text-xs"
                    :class="hasCompletionError || missingEvidence ? 'text-[#A83B32]' : completionData?.status === 'completed' ? 'text-[#35705A]' : 'text-[#716C65]'">
                    {{ compactStatus }}
                </p>
            </div>
            <Icon name="chevron" shared size="14" aria-hidden="true"
                class="shrink-0 text-[#3E6158] transition-transform duration-200"
                :class="expanded ? 'rotate-0' : '-rotate-90'" />
        </button>

        <div v-if="expanded" id="maintenance-completion-content"
            class="border-t border-[#312D32]/15 px-3 pb-3 md:px-4">
            <p v-if="loading" class="py-3 text-sm">Caricamento...</p>
            <template v-else-if="completionData">
                <p v-if="completionData.status === 'completed'" class="py-3 text-sm font-semibold text-[#35705A]">
                    Intervento completato.
                </p>
                <template v-else>
                    <div v-if="openDegradations.length" class="mt-3">
                        <div class="mb-1.5 flex items-center justify-between gap-3">
                            <p class="text-xs font-semibold uppercase text-[#716C65]">Degradi da risolvere</p>
                            <p class="text-xs font-semibold text-[#3E6158]">
                                {{ selectedIds.length }} di {{ openDegradations.length }} selezionati
                            </p>
                        </div>
                        <div class="divide-y divide-[#312D32]/10 border-y border-[#312D32]/15">
                            <label v-for="degradation in openDegradations" :key="degradation.id"
                                class="flex min-h-12 cursor-pointer items-center gap-2.5 border-l-4 px-2.5 py-1.5 transition-colors"
                                :class="isSelected(degradation.id)
                                    ? 'border-l-[#3E6158] bg-[#DDE7E2]'
                                    : 'border-l-transparent bg-white hover:bg-[#E9E7E1]'">
                                <input v-model="selectedIds" type="checkbox" :value="degradation.id"
                                    class="h-5 w-5 shrink-0 accent-[#3E6158]" />
                                <span class="min-w-0 flex-1">
                                    <span class="block truncate text-sm font-semibold">{{ degradation.title }}</span>
                                    <span class="block truncate text-xs text-[#716C65]">{{ degradation.typeName }}</span>
                                </span>
                                <span v-if="isSelected(degradation.id)"
                                    class="hidden shrink-0 text-[11px] font-bold uppercase text-[#3E6158] sm:inline">
                                    Selezionato
                                </span>
                            </label>
                        </div>
                    </div>
                    <p v-else class="mt-3 text-sm text-[#716C65]">Nessun degrado aperto collegato. L’intervento sarà completato senza chiusure di degradi mappati.</p>

                    <p v-if="conflicts.length" class="mt-2 text-xs font-semibold text-[#896A25]">
                        {{ conflicts.length }} degradi risultano già chiusi e non saranno sovrascritti.
                    </p>
                    <p v-if="missingEvidence" class="mt-2 text-xs font-semibold text-[#A83B32]">
                        Per completare l’intervento salva prima una data di fine e almeno una fotografia.
                    </p>

                    <textarea v-model="resolutionNotes" rows="2" placeholder="Note di risoluzione facoltative"
                        class="mt-3 w-full border border-[#312D32]/25 bg-white p-2.5 text-sm" />

                    <div class="mt-2 flex flex-wrap items-center justify-between gap-2">
                        <span class="text-xs text-[#716C65]">
                            {{ offline.online.value ? 'Online' : 'Offline: la conferma sarà inviata in seguito' }}
                        </span>
                        <button type="button" class="min-h-11 bg-[#3E6158] px-4 font-semibold text-white disabled:opacity-40"
                            :disabled="!canSubmit" @click="complete">
                            {{ submitting || offline.syncing.value ? 'Salvataggio...' : 'Completa intervento' }}
                        </button>
                    </div>
                </template>
            </template>

            <p v-if="hasCompletionError" class="mt-2 text-sm font-semibold text-[#A83B32]">
                {{ errorMessage || offline.lastError.value.message }}
            </p>
        </div>
    </section>
</template>